import { lazy, ComponentType } from 'react';

/**
 * sessionStorage key guarding against infinite reload loops when recovering
 * from stale lazy chunks after a deploy. Shared with the `vite:preloadError`
 * handler in main.tsx.
 */
export const CHUNK_RELOAD_GUARD_KEY = 'chunk-reload';

/**
 * Force a one-time full page reload to recover from a stale deployment
 * (old hashed chunks removed from the server). Returns true if a reload was
 * initiated, false if the guard is already set (we've reloaded once this
 * session already) or sessionStorage is unavailable.
 */
export function forceReloadOnce(): boolean {
  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) === '1') {
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, '1');
  } catch {
    // sessionStorage unavailable (some embedded WebViews) — don't risk a loop.
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * Core loader used by lazyWithRetry. Exported for unit testing.
 *
 * Retries the dynamic import with exponential back-off, treats an empty
 * module namespace as a chunk-load failure, and on final failure forces a
 * one-time page reload (stale-deploy recovery) instead of surfacing a
 * confusing TypeError to the ErrorBoundary.
 *
 * Why the empty-module check: Vite's `__vitePreload` dispatches a cancelable
 * `vite:preloadError` event when a dynamic import fails. If any listener
 * calls `event.preventDefault()`, the failed import RESOLVES with `undefined`
 * instead of rejecting. Downstream `.then(m => ({ default: m.X }))` mappers
 * then throw "Cannot read properties of undefined (reading 'X')" — see
 * GitHub issue #737.
 */
export async function loadWithRetry<T>(
  importFn: () => Promise<{ default: T }>,
  maxRetries = 2,
  delayFn: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms))
): Promise<{ default: T }> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const module = await importFn();
      if (!module || module.default == null) {
        throw new TypeError(
          'Dynamic import resolved without a module — likely a stale chunk after a deploy'
        );
      }
      return module;
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries) {
        // Exponential back-off: 500ms, 1000ms, ...
        await delayFn(500 * (attempt + 1));
      }
    }
  }

  // All retries failed — almost certainly a stale deployment (the old hashed
  // chunk no longer exists). Reload once to pick up the new index.html.
  if (forceReloadOnce()) {
    // Reload is in flight. Never resolve so Suspense keeps showing its
    // fallback — no ErrorBoundary flash and no bogus error report for a
    // self-healing condition.
    return new Promise<never>(() => {});
  }

  // Already reloaded once this session (or sessionStorage unavailable) and
  // the chunk still fails — surface the real error to the ErrorBoundary.
  throw lastError;
}

/**
 * Wraps React.lazy with automatic retry logic for chunk-loading failures.
 *
 * When code-splitting produces dynamic chunks, a network flake or stale
 * deployment can cause the initial import() to fail. This wrapper retries
 * the import up to `maxRetries` times with an exponential back-off, and if
 * the chunk is still unavailable it forces a one-time page reload (guarded
 * against loops via sessionStorage) before giving up and throwing to the
 * nearest ErrorBoundary.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  maxRetries = 2
) {
  return lazy(() => loadWithRetry(importFn, maxRetries));
}
